import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PLAN_TYPE } from '../constants/subscriptionTypes';
import * as localStorageService from '../services/local-storage-service';

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add a separate effect to log the subscription when it changes
  useEffect(() => {
    if (subscription) {
      console.log('DEBUG - Subscription state updated:', subscription);
    }
  }, [subscription]);

  useEffect(() => {
    async function fetchSubscription() {
      if (!user) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // First check if we have user data in localStorage using the service
        const userData = localStorageService.getUserData(true, true); // validateTimestamp=true, debug=true
        
        // Define a function to process user data (to avoid code duplication)
        const processUserData = (userData) => {
          console.log('Processing user data with subscription type:', userData.stripeSubscriptionType);
          const rawType = userData.stripeSubscriptionType || PLAN_TYPE.FREE;
          // Enhanced type conversion to handle more variations
          let type;
          if (rawType === 'year' || rawType === 'yearly' || rawType === PLAN_TYPE.YEARLY) {
            type = PLAN_TYPE.YEARLY;
          } else if (rawType === 'month' || rawType === 'monthly' || rawType === PLAN_TYPE.MONTHLY) {
            type = PLAN_TYPE.MONTHLY;
          } else if (rawType === 'canceled' || rawType === PLAN_TYPE.CANCELED) {
            type = PLAN_TYPE.CANCELED;
          } else if (!rawType || rawType === 'Free plan' || rawType === PLAN_TYPE.FREE) {
            type = PLAN_TYPE.FREE;
          } else {
            // For any other value, use as is
            type = rawType;
          }
          setSubscription({
            type,
            subscriptionDate: userData.subscriptionDate || null,
            nextPaymentDate: userData.nextPaymentDate || null,
            currentPlanValidTo: userData.currentPlanValidTo || null,
            priceId: userData.stripePriceId || null,
            stripeCustomerId: userData.stripeCustomerId || null,
            canceledDate: userData.stripeCanceledDate || null
          });
        };
        
        if (userData) {
          // Use the stored data
          console.log('useSubscription: Using user data from localStorage');
          console.log('DEBUG - Using stored userData:', userData);
          processUserData(userData);
          // Can't log subscription here as state hasn't updated yet
          setLoading(false);
          return;
        }
        
        // If we don't have valid stored data, trigger a re-authentication through AuthContext
        console.log('useSubscription: No valid stored data found, triggering re-authentication');
        
        // Show a temporary loading state
        setError('Refreshing user data...');
        
        // We'll use a simple approach to trigger the auth flow again
        // This will cause the AuthContext to fetch fresh data from the cloud function
        try {
          // Clear auth-related localStorage items to force a refresh
          localStorageService.clearUserData();
          
          // Set a flag to indicate we're refreshing data
          localStorageService.setForceAuthRefresh();
          
          // Reload the page to trigger the auth flow
          // This is a simple but effective way to refresh the data
          // The AuthContext will handle the API call on page load
          window.location.reload();
          
          // While the page is reloading, show a temporary state
          setSubscription({ type: PLAN_TYPE.FREE, isRefreshing: true });
        } catch (err) {
          console.error('Error triggering re-authentication:', err);
          setError('Failed to refresh user data. Please try logging in again.');
          setSubscription({ type: PLAN_TYPE.FREE });
        }
      } catch (err) {
        console.error('Error fetching subscription data:', err);
        setError('Failed to load subscription information');
        setSubscription({ type: PLAN_TYPE.FREE });
      } finally {
        setLoading(false);
      }
    }

    fetchSubscription();
  }, [user]);

  return { subscription, loading, error };
}
